/**
 * The shop's unit words, in English.
 *
 * Units are free text on the product (`unit_of_measure`) and on each packaging
 * row (`item_units.name`), typed by the shop in Arabic — so an English invoice
 * printed «قطعة» in the الوحدة column and a foreign customer read nothing. The
 * vocabulary is closed and small (ten base units, fifteen packagings across the
 * whole catalogue), so it is a dictionary, not a translation service.
 *
 * DELIBERATELY INCOMPLETE. A word with no crisp English equivalent is returned
 * unchanged rather than guessed at: a wrong unit on an invoice is an argument
 * about how much was delivered, and the Arabic word is at least the truth. Add
 * to the table only what the shop confirms.
 */

/**
 * Zero-width joiners, bidi marks, BOM and the non-breaking space — written as
 * escapes on purpose: as literal characters they are invisible in every editor
 * and diff, which is how they survive in pasted data to begin with.
 */
const INVISIBLE = /[\u00A0\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/** Fold the letter variants that differ between keyboards but mean one letter. */
function fold(s: string): string {
  return s
    .replace(INVISIBLE, ' ')
    .replace(/[آأإٱ]/g, 'ا') // آ أ إ ٱ → ا
    .replace(/[یى]/g, 'ي') // ی ى → ي
    .replace(/ک/g, 'ك') // ک → ك
    .replace(/ة/g, 'ه') // ة → ه
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Arabic (folded) → English. Anything absent prints as it was typed. */
const EN: Record<string, string> = {
  [fold('قطعة')]: 'Piece',
  [fold('درزن')]: 'Dozen',
  [fold('كرتون')]: 'Carton',
  [fold('كارتون')]: 'Carton',
  [fold('باكيت')]: 'Pack',
  [fold('كيس')]: 'Bag',
  [fold('علبة')]: 'Box',
  [fold('رولة')]: 'Roll',
  [fold('ستاند')]: 'Stand',
  [fold('سيت')]: 'Set',
  [fold('دست')]: 'Set',
  [fold('بند')]: 'Bundle',
  [fold('غم')]: 'g',
  [fold('كغم')]: 'kg',
  [fold('متر')]: 'm',
  [fold('لتر')]: 'L',
};

/**
 * A unit as it should read on paper in `locale`.
 *
 * Arabic keeps the shop's own word untouched. English translates a known word,
 * including one carrying a count — «باكيت 10» becomes «Pack 10» — and leaves
 * everything else exactly as entered.
 */
export function unitLabel(unit: string | null | undefined, locale: string): string {
  if (!unit) return '';
  if (!locale.toLowerCase().startsWith('en')) return unit;
  const whole = EN[fold(unit)];
  if (whole) return whole;
  // "باكيت 10" / "قطعة ×12": translate the word, keep what qualifies it.
  const m = /^(\S+)\s+(.+)$/.exec(unit.trim());
  const head = m?.[1] ? EN[fold(m[1])] : undefined;
  return head && m?.[2] ? `${head} ${m[2]}` : unit;
}
